import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSpace, OPEN_ROOM_REFETCH_MS } from '../lib/api';
import type { ApiError, Message } from '../lib/api';
import { spaceDisplayName } from '../lib/spaceIdentity';
import { Composer } from './Composer';
import type { ReplyContext } from './Composer';
import { MessageThread } from './MessageThread';
import { Skeleton } from './Skeleton';
import { SpaceHeader } from './SpaceHeader';

interface SpaceViewProps {
  roomId: string;
}

export function SpaceView({ roomId }: SpaceViewProps) {
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['space', roomId],
    queryFn: () => fetchSpace(roomId),
    // Fail fast on 4xx (not found / forbidden): retrying can't fix a client
    // error, and the default 3 retries left the user staring at the loading
    // skeleton for seconds before the error surfaced. Still retry transient
    // 5xx / network failures a couple of times.
    retry: (failureCount, error) => {
      const status = (error as unknown as ApiError | undefined)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    // Keep the open room's header/members/state live alongside its messages.
    refetchInterval: OPEN_ROOM_REFETCH_MS,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [replyTo, setReplyTo] = useState<ReplyContext | null>(null);

  const handleReply = useCallback((msg: Message) => {
    setReplyTo({
      messageId: msg.message_id,
      senderPersona: msg.sender_persona,
      preview: msg.content,
    });
  }, []);

  const handleCancelReply = useCallback(() => setReplyTo(null), []);

  const handleSearchChange = useCallback(
    (query: string) => setSearchQuery(query),
    [],
  );

  if (isLoading) {
    return (
      <div className="space-view-loading">
        <Skeleton
          className="space-view-loading-skeleton"
          ariaLabel="Loading space"
          rows={4}
          variant="text"
        />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-view-error" role="alert">
        <p className="space-view-error-message">Could not load this space.</p>
        <button
          type="button"
          className="space-view-error-retry"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  const composerDisabled = data.state !== 'active';
  const composerDisabledReason = `This space is ${data.state}. Messaging is disabled.`;
  const spaceName = spaceDisplayName(data.description, data.members);

  return (
    <section className="space-view" aria-label={`Space: ${data.description}`}>
      <SpaceHeader room={data} onSearchChange={handleSearchChange} />
      <MessageThread
        roomId={roomId}
        searchQuery={searchQuery}
        onReply={handleReply}
        messagingDisabled={composerDisabled}
        members={data.members}
      />
      <Composer
        roomId={roomId}
        disabled={composerDisabled}
        disabledReason={composerDisabledReason}
        replyTo={replyTo}
        onCancelReply={handleCancelReply}
        placeholder={`Message ${spaceName}…`}
      />
    </section>
  );
}

